import { memo, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { User, Building2, Briefcase, Video } from "lucide-react";
import { DoctorCard } from "./DoctorCard";
import type { Doctor, DoctorType } from "@/types/schema";
import { Button } from "@/components/ui/button";
import { SmoothLink as Link } from "@/components/navigation";

interface DoctorsListProps {
  doctors: Doctor[];
}

export const DoctorsList = memo(function DoctorsList({ doctors }: DoctorsListProps) {
  // Group doctors by type
  const groupedDoctors = useMemo(() => {
    const groups: Record<DoctorType, Doctor[]> = {
      hospital_staff: [],
      independent: [],
      consultant: [],
    };
    
    doctors.forEach(doctor => {
      const type = doctor.doctor_type || 'hospital_staff';
      if (groups[type]) {
        groups[type].push(doctor);
      }
    });
    
    return groups;
  }, [doctors]);

  if (doctors.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center space-y-4">
          <div className="p-3 bg-primary/10 rounded-full w-fit mx-auto">
            <User className="w-8 h-8 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold text-lg text-secondary">Specialist Match</h2>
            <p className="text-muted-foreground max-w-md mx-auto">
              Our medical team will match you with the best doctor for your specific needs.
            </p>
          </div>
          <Link to="/contact/">
            <Button className="rounded-full">Request Specialist Match</Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle as="h2" className="flex items-center gap-2">
          <User className="w-5 h-5 text-primary" />
          Expert Doctors ({doctors.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Hospital Staff Doctors */}
        {groupedDoctors.hospital_staff.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Building2 className="w-4 h-4 text-secondary" />
              <h3 className="font-medium text-secondary">Hospital Doctors</h3>
              <Badge variant="secondary" className="text-xs">
                {groupedDoctors.hospital_staff.length}
              </Badge>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {groupedDoctors.hospital_staff.map((doctor) => (
                <DoctorCard key={doctor.id} doctor={doctor} />
              ))}
            </div>
          </div>
        )}

        {/* Independent Doctors */}
        {groupedDoctors.independent.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Briefcase className="w-4 h-4 text-primary" />
              <h3 className="font-medium text-secondary">Independent Specialists</h3>
              <Badge variant="outline" className="text-xs">
                {groupedDoctors.independent.length}
              </Badge>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {groupedDoctors.independent.map((doctor) => (
                <DoctorCard key={doctor.id} doctor={doctor} />
              ))}
            </div>
          </div>
        )}

        {/* Online Consultants */}
        {groupedDoctors.consultant.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Video className="w-4 h-4 text-accent" />
              <h3 className="font-medium text-secondary">Online Consultants</h3>
              <Badge className="text-xs bg-accent/10 text-accent border-accent/30">
                {groupedDoctors.consultant.length}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              Available for video consultations and online guidance
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {groupedDoctors.consultant.map((doctor) => (
                <DoctorCard key={doctor.id} doctor={doctor} />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
});
